/**
 * What the company's automated outreach chain will do with a contact the
 * recruiter is entering, said only as far as it is certain.
 *
 * Duplicate outreach is the biggest drag on the autonomous-verification
 * metric: at clients with the chain on, recruiters send hundreds of manual
 * emails a week to employers the chain is already emailing, and an employer
 * answer after a manual touch counts as ASSISTED. The fix is to say, at the
 * moment the address lands in the field, that automation has it.
 *
 * Deliberately no timings and no step counts. Business-day rules, a
 * rescheduled or cancelled step, or a config change after the entry is saved
 * would make "then again in 4 days" a promise the chain does not keep. The
 * real schedule is the VoE drawer's audit trail (and its admin-only scheduled
 * actions table), which reflects the actual rows.
 */
import {
  VoEOutreachTrigger,
  VoEOutreachType,
} from 'src/graphql/types/verificationOfEmployments'

export interface OutreachConfigLike {
  enabled?: boolean | null
  outreachTrigger?: string | null
  skipCurrentEmployer?: boolean | null
  steps?: Array<{ type?: string | null }> | null
}

export interface OutreachPreview {
  /** will: automation is going to act; wait: it is waiting on the recruiter. */
  tone: 'will' | 'wait'
  text: string
}

/**
 * Null means nothing to say: automation is off, has no steps, only runs from
 * automation rules, has no step that can reach the contact as entered
 * (email steps with no email address, call steps with no phone), or the
 * company skips current employers and this is one.
 */
export const outreachPreview = (
  config: OutreachConfigLike | null | undefined,
  contact: { hasEmail: boolean; hasPhone: boolean; isCurrentEmployer?: boolean }
): OutreachPreview | null => {
  if (!config?.enabled) return null
  if (config.skipCurrentEmployer && contact.isCurrentEmployer) return null
  const steps = config.steps ?? []
  if (steps.length === 0) return null
  if (config.outreachTrigger === VoEOutreachTrigger.AUTOMATION_ONLY) return null

  const canEmail =
    contact.hasEmail && steps.some((s) => s.type === VoEOutreachType.EMAIL)
  const canCall =
    contact.hasPhone && steps.some((s) => s.type === VoEOutreachType.CALL)
  if (!canEmail && !canCall) return null

  if (config.outreachTrigger === VoEOutreachTrigger.AFTER_MANUAL) {
    return {
      tone: 'wait',
      text: 'Automated follow-ups start after your first outreach. Log it on the VoE tab.',
    }
  }

  const verb = canEmail ? 'email' : 'call'
  return {
    tone: 'will',
    text: `Automated outreach will ${verb} this contact. You don't need to send one.`,
  }
}
